import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lightbulb, Loader2, RefreshCw, Check, X, CheckCheck, ThumbsUp, ThumbsDown, UserCheck } from "lucide-react";

const STATUSES = [["open", "Open"], ["acknowledged", "Acknowledged"], ["dismissed", "Dismissed"], ["resolved", "Resolved"]];
const SEV = { critical: "bg-rose-100 text-rose-700", high: "bg-orange-100 text-orange-700", warning: "bg-amber-100 text-amber-700", opportunity: "bg-sky-100 text-sky-700", info: "bg-slate-100 text-slate-600" };
export const CLASS = {
  verified_risk: ["Verified risk", "bg-rose-50 text-rose-700 border-rose-200"],
  needs_verification: ["Needs verification", "bg-amber-50 text-amber-800 border-amber-200"],
  opportunity: ["Opportunity", "bg-sky-50 text-sky-700 border-sky-200"],
};
export const ClassChip = ({ c, testid }) => {
  const [label, cls] = CLASS[c] || CLASS.verified_risk;
  return <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${cls}`} data-testid={testid}>{label}</span>;
};
const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB") : "");
const NEXT = { acknowledge: "acknowledged", dismiss: "dismissed", resolve: "resolved", reopen: "open" };

function Records({ id, total }) {
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/intel/insights/${id}/records`, { params: { page, size: 25 } }).then((r) => setD(r.data)); }, [id, page]);
  if (!d) return <Loader2 className="w-4 h-4 animate-spin" />;
  const pages = Math.max(1, Math.ceil(d.total / d.size));
  return (
    <div className="border rounded-md bg-white overflow-x-auto" data-testid="insight-records">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 text-slate-500"><tr><th className="text-left p-2">Record</th><th className="text-left p-2">Current status</th><th className="text-left p-2">Age</th><th className="text-left p-2">Assigned</th></tr></thead>
        <tbody>
          {d.rows.map((r, i) => (
            <tr key={`${r.type}-${r.id}-${i}`} className="border-t" data-testid={`insight-record-${r.type}-${r.id}`}>
              <td className="p-2">{r.link ? <Link to={r.link} className="text-indigo-600 hover:underline">{r.label || r.id}</Link> : (r.label || r.id)}<span className="block text-[10px] text-slate-400">{r.type} {r.id}</span></td>
              <td className="p-2">{r.status || "-"}</td>
              <td className="p-2 whitespace-nowrap">{r.age_days != null ? `${Math.round(r.age_days)} d` : "-"}</td>
              <td className="p-2">{r.assignee || "Unassigned"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {pages > 1 && (
        <div className="flex items-center gap-2 p-2 text-xs" data-testid="insight-records-pager">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="insight-records-prev">Prev</Button>
          <span>Page {page} of {pages} · {d.complete ? `${total} records` : `showing first ${d.listed} of ${total} records`}</span>
          <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="insight-records-next">Next</Button>
        </div>
      )}
    </div>
  );
}

export function InsightDetail({ id, onChanged = () => {}, onAsk = null }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => { api.get(`/intel/insights/${id}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (a) => {
    try { await api.post(`/intel/insights/${id}/actions/${a}`, { note }); setNote(""); load(); onChanged(NEXT[a]); toast.success("Updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const fb = async (useful) => {
    try { await api.post(`/intel/insights/${id}/feedback`, { useful, reason: note }); setNote(""); load(); toast.success("Thanks for the feedback"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save feedback"); }
  };
  if (!d) return <div className="p-4"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  if (d.error) return <p className="p-4 text-sm text-slate-500" data-testid="insight-detail-unavailable">This insight is no longer available to you.</p>;
  return (
    <div className="p-4 space-y-3 bg-slate-50 border-t" data-testid="insight-detail">
      {d.detail && <p className="text-sm text-slate-700">{d.detail}</p>}
      {d.group && <p className="text-[11px] text-slate-500" data-testid="insight-group-note">Group of {d.affected_count} records. Acknowledging or dismissing the group never changes the records themselves. The count updates each check; new records reopen the group and it resolves when none are left.</p>}
      <Records id={id} total={d.affected_count} />
      {d.data_gaps?.length > 0 && <p className="text-xs text-amber-700">Missing data: {d.data_gaps.join(", ")}</p>}
      <div className="text-[11px] text-slate-500">Source: {d.source} · rule {d.rule} · first seen {fmt(d.first_seen)} · last seen {fmt(d.last_seen)}{d.owner ? ` · owner ${d.owner.name}` : ""}</div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note or reason (required to dismiss)" className="w-full text-sm border rounded-md px-2 py-1.5" data-testid="insight-note-input" />
      <div className="flex flex-wrap gap-2">
        {!d.owner && <Button size="sm" variant="outline" onClick={() => act("claim")} data-testid="insight-claim-btn"><UserCheck className="w-3.5 h-3.5 mr-1" />Take ownership</Button>}
        {d.owner && <Button size="sm" variant="outline" onClick={() => act("release")} data-testid="insight-release-btn">Release ownership</Button>}
        {d.status === "open" && <Button size="sm" variant="outline" onClick={() => act("acknowledge")} data-testid="insight-ack-btn"><Check className="w-3.5 h-3.5 mr-1" />Acknowledge</Button>}
        {d.status !== "resolved" && <Button size="sm" variant="outline" onClick={() => act("resolve")} data-testid="insight-resolve-btn"><CheckCheck className="w-3.5 h-3.5 mr-1" />Resolve</Button>}
        {d.status !== "dismissed" && <Button size="sm" variant="outline" onClick={() => act("dismiss")} data-testid="insight-dismiss-btn"><X className="w-3.5 h-3.5 mr-1" />Dismiss</Button>}
        {d.status !== "open" && <Button size="sm" variant="ghost" onClick={() => act("reopen")} data-testid="insight-reopen-btn">Reopen</Button>}
        <Button size="sm" variant="ghost" onClick={() => fb(true)} data-testid="insight-useful-btn"><ThumbsUp className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="ghost" onClick={() => fb(false)} data-testid="insight-not-useful-btn"><ThumbsDown className="w-3.5 h-3.5" /></Button>
        {onAsk && <Button size="sm" variant="outline" onClick={() => onAsk(`Explain insight "${d.title}" (id ${d.id}) and which records support it.`)} data-testid="insight-ask-ted-btn">Ask Ted about this</Button>}
      </div>
      <ol className="text-[11px] text-slate-500 space-y-0.5" data-testid="insight-history">
        {(d.events || []).map((e) => <li key={e.id}>{fmt(e.at)} · {e.kind} · {e.by}{e.note ? ` · ${e.note}` : ""}</li>)}
      </ol>
    </div>
  );
}

function Row({ it, open, onToggle, onChanged, onAsk }) {
  return (
    <li className="border rounded-lg bg-white overflow-hidden" data-testid={`insight-row-${it.id}`}>
      <button type="button" onClick={onToggle} className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50">
        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${SEV[it.severity] || ""}`}>{it.severity}</span>
        <ClassChip c={it.classification} testid={`insight-class-${it.id}`} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-slate-800">{it.title}</span>
          <span className="block text-[11px] text-slate-500">{it.department} · {it.affected_count ?? (it.evidence || []).length} record(s){it.backlog ? " · historical backlog" : ""} · priority {it.priority}{it.owner ? ` · ${it.owner.name}` : ""}</span>
        </span>
      </button>
      {open && <InsightDetail id={it.id} onChanged={onChanged} onAsk={onAsk} />}
    </li>
  );
}

export function InsightsBoard({ onAsk, compact = false }) {
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(() => new URLSearchParams(window.location.search).get("insight"));
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/intel/insights", { params: { status } }).then((r) => setData(r.data)).catch((e) => setData({ error: e.response?.status }));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setBusy(true);
    try { const r = await api.post("/intel/run"); toast.success(r.data.skipped ? `Skipped: ${r.data.skipped}` : `Check complete: ${r.data.insights_seen ?? 0} finding(s), ${r.data.created ?? 0} new`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not run"); }
    setBusy(false);
  };
  if (data?.error === 404) return <div className="p-6 text-sm text-slate-500" data-testid="ted-insights-off">No insights to show: the Insight Engine is switched off for this company, or its findings are in review and not yet shared with staff.</div>;
  return (
    <div className={compact ? "space-y-3" : "p-4 md:p-6 max-w-5xl space-y-4"} data-testid="ted-insights-page">
      <div className="flex flex-wrap items-center gap-3">
        <Lightbulb className={compact ? "w-5 h-5 text-amber-500" : "w-6 h-6 text-amber-500"} />
        {compact ? <h2 className="text-base md:text-lg font-semibold text-slate-800">Insight register</h2> : <h1 className="text-2xl font-semibold text-slate-900">Ted Insights</h1>}
        <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600" data-testid="ted-insights-mode">{data?.review_only ? "Review-only · visible to you only · no alerts sent" : "Shadow mode · no alerts sent"}</span>
        <div className="flex-1" />
        {data?.reviewer && (
        <Button size="sm" variant="outline" onClick={runNow} disabled={busy} data-testid="ted-insights-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}<span className="ml-1">Check now</span>
        </Button>
        )}
      </div>
      <p className="text-xs text-slate-500" data-testid="ted-insights-last-run">Every item comes from your own records. Last checked: {data?.last_run ? fmt(data.last_run) : "not yet"}</p>
      <div className="flex flex-wrap gap-1.5" data-testid="ted-insights-status-filter">
        {STATUSES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`ted-insights-status-${k}`}
            className={`text-xs px-3 py-1 rounded-full border ${status === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600"}`}>
            {l} {data?.counts?.[k] ?? ""}
          </button>
        ))}
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : data.items?.length === 0 ? (
        <div className="text-sm text-slate-500 border rounded-lg p-6 bg-white" data-testid="ted-insights-empty">Nothing to show here.</div>
      ) : (
        <ul className="space-y-2" data-testid="ted-insights-list">
          {data.items.map((it) => <Row key={it.id} it={it} open={openId === it.id} onToggle={() => setOpenId(openId === it.id ? null : it.id)} onChanged={(next) => { if (next && next !== status) { setStatus(next); } else { load(); } }} onAsk={onAsk} />)}
        </ul>
      )}
    </div>
  );
}

export default function TedInsights() {
  const [ted, setTed] = useState(null);
  useEffect(() => { api.get("/ted/status").then((r) => setTed(!!(r.data.enabled && r.data.unified))).catch(() => setTed(false)); }, []);
  if (ted === null) return null;
  if (ted) return <Navigate to={`/ted?area=insights${window.location.search.replace("?", "&")}`} replace />;
  return <InsightsBoard />;
}
